import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import itineraryMapMarkerObject from '@/entities/itineraryMapMarker/interface'

import {
  ALL_ITEMS_QUERY,
  SINGLE_ITEM_QUERY,
  ITEMS_DEPENDENTS_QUERY,
  CREATE_ITEM_MUTATION,
  UPDATE_ITEM_MUTATION,
  DELETE_ITEM_MUTATION,
  FORCE_DELETE_ITEM_MUTATION,
  ADD_ITEM_MAP_MARKERS_MUTATION,
  REMOVE_ITEM_MAP_MARKERS_MUTATION,
  CREATE_ITEM_MAP_MARKERS_MUTATION,
  DELETE_ITEM_MAP_MARKERS_MUTATION,
  UPDATE_ITEM_MAP_MARKERS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const itineraryItemRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ITEM_QUERY, variables).then((data: any) => {
      return hydrateObject(data.item)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ITEMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.items)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ITEM_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.item),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ITEMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.items),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ITEMS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ITEMS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ITEM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.item)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ITEM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.item)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ITEM_MUTATION, variables).then((data: any) => {
      return data.deleteItineraryItem
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ITEM_MUTATION, variables).then((data: any) => {
      return data.forceDeleteItineraryItem
    })
  },

  assignMapMarkers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        mapMarkerIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ITEM_MAP_MARKERS_MUTATION, variables)
  },

  unassignMapMarkers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        mapMarkerIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ITEM_MAP_MARKERS_MUTATION, variables)
  },

  createMapMarkers: async (id: string, items: Array<itineraryMapMarkerObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        mapMarkers: items,
      },
    }
    return graphQLClient.request(CREATE_ITEM_MAP_MARKERS_MUTATION, variables)
  },

  deleteMapMarkers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        mapMarkerIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ITEM_MAP_MARKERS_MUTATION, variables)
  },

  updateMapMarkersOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        mapMarkerIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ITEM_MAP_MARKERS_ORDER_MUTATION, variables)
  },
}

export default itineraryItemRepository
